'use strict'

// Image columns store the file bytes plus `{uniqueTitle}_type` and
// `{uniqueTitle}_filename`. A spreadsheet cell editor sends
// `{ data, contentType, filename }` where `data` is base64. This module
// stays free of Node's Buffer so the designer can import it.

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

function isStoredBinary (value) {

  return typeof Uint8Array !== 'undefined' && value instanceof Uint8Array
}

function isImageUpload (value) {

  if (!value || typeof value !== 'object' || isStoredBinary(value)) return false
  if (value.type === 'Buffer') return false
  if (typeof value.data !== 'string' || !value.data) return false

  return typeof value.filename === 'string' || typeof value.contentType === 'string'
}

function rawBase64 (data) {

  const text = String(data || '').replace(/\s/g, '')
  const comma = text.indexOf(',')

  if (text.slice(0, 5) === 'data:' && comma >= 0) return text.slice(comma + 1)

  return text
}

function base64ToBytes (data) {

  const clean = rawBase64(data)

  if (!clean || clean.length % 4 !== 0) return null

  const bytes = []

  for (let i = 0; i < clean.length; i += 4) {

    const n0 = BASE64_ALPHABET.indexOf(clean[i])
    const n1 = BASE64_ALPHABET.indexOf(clean[i + 1])
    const n2 = clean[i + 2] === '=' ? -1 : BASE64_ALPHABET.indexOf(clean[i + 2])
    const n3 = clean[i + 3] === '=' ? -1 : BASE64_ALPHABET.indexOf(clean[i + 3])

    if (n0 < 0 || n1 < 0 || n2 === -2 || n3 === -2) return null
    if ((n2 < 0 && clean[i + 2] !== '=') || (n3 < 0 && clean[i + 3] !== '=')) return null

    const triple = (n0 << 18) | (n1 << 12) | ((n2 < 0 ? 0 : n2) << 6) | (n3 < 0 ? 0 : n3)

    bytes.push((triple >> 16) & 255)
    if (n2 >= 0) bytes.push((triple >> 8) & 255)
    if (n3 >= 0) bytes.push(triple & 255)
  }

  return bytes
}

function bytesToBase64 (bytes) {

  if (!bytes || !bytes.length) return ''

  let out = ''

  for (let i = 0; i < bytes.length; i += 3) {

    const a = bytes[i]
    const b = i + 1 < bytes.length ? bytes[i + 1] : 0
    const c = i + 2 < bytes.length ? bytes[i + 2] : 0
    const triple = (a << 16) | (b << 8) | c

    out += BASE64_ALPHABET[(triple >> 18) & 63]
    out += BASE64_ALPHABET[(triple >> 12) & 63]
    out += i + 1 < bytes.length ? BASE64_ALPHABET[(triple >> 6) & 63] : '='
    out += i + 2 < bytes.length ? BASE64_ALPHABET[triple & 63] : '='
  }

  return out
}

function byteList (value) {

  if (!value) return null
  if (isStoredBinary(value)) return Array.from(value)
  if (value.type === 'Buffer' && Array.isArray(value.data)) return value.data.slice()

  return null
}

function imageUploadFromDataUrl (dataUrl, filename, contentType) {

  const text = String(dataUrl || '')
  const comma = text.indexOf(',')
  const meta = comma >= 0 ? text.slice(0, comma) : ''
  const matched = /^data:([^;,]+)/.exec(meta)
  const typeFromUrl = matched && matched[1]

  return {
    contentType: contentType || typeFromUrl || 'application/octet-stream',
    filename: filename || 'image',
    data: comma >= 0 ? text.slice(comma + 1) : text
  }
}

function positivePixel (value) {

  const number = Number(value)

  if (!Number.isFinite(number) || number <= 0) return null

  return Math.round(number)
}

// Stored on an image column. Resize runs only when auto-resize is on and
// both sides are a positive pixel count.
function imageResizeFields (columnType, source) {

  if (columnType !== 'image') {

    return {
      autoResize: false,
      resizeWidth: null,
      resizeHeight: null,
      keepProportions: false
    }
  }

  const input = source || {}

  return {
    autoResize: !!input.autoResize,
    resizeWidth: positivePixel(input.resizeWidth),
    resizeHeight: positivePixel(input.resizeHeight),
    keepProportions: !!input.keepProportions
  }
}

function imageResizeSettings (column) {

  const fields = imageResizeFields(column && column.type, column)

  if (!fields.autoResize || !fields.resizeWidth || !fields.resizeHeight) return null

  return {
    width: fields.resizeWidth,
    height: fields.resizeHeight,
    keepProportions: fields.keepProportions
  }
}

// Exact width and height when proportions are not kept. Otherwise the
// largest size that stays inside both limits, scaled evenly.
function fittedImageSize (sourceWidth, sourceHeight, settings) {

  const width = positivePixel(sourceWidth)
  const height = positivePixel(sourceHeight)

  if (!width || !height || !settings) return null

  const maxWidth = positivePixel(settings.width)
  const maxHeight = positivePixel(settings.height)

  if (!maxWidth || !maxHeight) return null

  if (!settings.keepProportions) return { width: maxWidth, height: maxHeight }

  const scale = Math.min(maxWidth / width, maxHeight / height)
  let nextWidth = Math.max(1, Math.round(width * scale))
  let nextHeight = Math.max(1, Math.round(height * scale))

  if (nextWidth > maxWidth) nextWidth = maxWidth
  if (nextHeight > maxHeight) nextHeight = maxHeight

  return { width: nextWidth, height: nextHeight }
}

function imageFieldKeys (uniqueTitle) {

  return [`${uniqueTitle}_type`, `${uniqueTitle}_filename`]
}

function imageFilename (row, uniqueTitle) {

  if (!row || !uniqueTitle) return ''

  const name = row[`${uniqueTitle}_filename`]

  if (name == null || name === '') return ''

  return String(name)
}

function imageDataUrl (row, uniqueTitle) {

  if (!row || !uniqueTitle) return ''

  const bytes = byteList(row[uniqueTitle])

  if (!bytes || !bytes.length) return ''

  const type = row[`${uniqueTitle}_type`] || 'application/octet-stream'

  return `data:${type};base64,${bytesToBase64(bytes)}`
}

const VIEW_IMAGE_STYLE = 'max-width:160px;max-height:160px;object-fit:cover;vertical-align:middle'

function escapeAttribute (value) {

  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function unescapeAttribute (value) {

  return String(value || '')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
}

// HTML for a stored image. Views that use v-html can drop this in as-is.
function imageMarkup (row, uniqueTitle) {

  const src = imageDataUrl(row, uniqueTitle)

  if (!src) return ''

  const name = imageFilename(row, uniqueTitle)
  const alt = name ? ` alt="${escapeAttribute(name)}"` : ''

  return `<img class="view-image" src="${escapeAttribute(src)}"${alt} style="${VIEW_IMAGE_STYLE}" />`
}

function isViewImageMarkup (value) {

  return typeof value === 'string' && value.indexOf('<img class="view-image" ') === 0
}

function imageMarkupSource (value) {

  if (!isViewImageMarkup(value)) return ''

  const match = /src="([^"]*)"/.exec(value)

  return match ? unescapeAttribute(match[1]) : ''
}

function fileDownloadMarkup (row, uniqueTitle) {

  const src = imageDataUrl(row, uniqueTitle)

  if (!src) return ''

  const name = imageFilename(row, uniqueTitle) || 'Download'

  return `<a class="view-file" href="${escapeAttribute(src)}" download="${escapeAttribute(name)}">${escapeAttribute(name)}</a>`
}

// An <img> for stored image bytes, a download link for any other file, otherwise null.
function imageCellHtml (row, uniqueTitle) {

  if (!row || !uniqueTitle) return null
  if (!byteList(row[uniqueTitle])) return null

  const type = row[`${uniqueTitle}_type`]

  if (type && String(type).toLowerCase().indexOf('image/') !== 0) {

    return fileDownloadMarkup(row, uniqueTitle)
  }

  return imageMarkup(row, uniqueTitle)
}

// Rewrites image-column entries on a create or update body.
// `toStoredBytes` turns a byte list into the value SQLite should store.
// A binary value already on the body (a form upload) is left in place.
// A plain string is removed so the text cell editor cannot write into the blob.
function applyImageUploads (columns, values, toStoredBytes) {

  if (!values || !Array.isArray(columns)) return values

  const store = toStoredBytes || ((bytes) => bytes)

  for (const column of columns) {

    if (!column || (column.type !== 'image' && column.type !== 'file') || !column.uniqueTitle) continue
    if (!Object.prototype.hasOwnProperty.call(values, column.uniqueTitle)) continue

    const key = column.uniqueTitle
    const incoming = values[key]

    if (incoming === null) {

      values[key] = null
      values[`${key}_type`] = null
      values[`${key}_filename`] = null
      continue
    }

    if (isStoredBinary(incoming)) continue

    const echoed = byteList(incoming)

    if (echoed) {

      values[key] = store(echoed)
      continue
    }

    if (!isImageUpload(incoming)) {

      delete values[key]
      continue
    }

    const bytes = base64ToBytes(incoming.data)

    if (!bytes || !bytes.length) {

      delete values[key]
      continue
    }

    values[key] = store(bytes)
    values[`${key}_type`] = incoming.contentType || 'application/octet-stream'
    values[`${key}_filename`] = incoming.filename || (column.type === 'file' ? 'file' : 'image')
  }

  return values
}

module.exports = {
  isImageUpload,
  imageUploadFromDataUrl,
  imageResizeFields,
  imageResizeSettings,
  fittedImageSize,
  imageFieldKeys,
  imageFilename,
  imageDataUrl,
  imageMarkup,
  isViewImageMarkup,
  imageMarkupSource,
  imageCellHtml,
  applyImageUploads,
  base64ToBytes,
  bytesToBase64
}
